import { Editor } from 'tinymce';
import { plugins, toolbar, styles } from './settings';
import {
  blockquote,
  charcount,
  infobox,
  placeholder,
  parallax,
  paywall,
} from './setupEditor';

export type Setup = {
  base: object;
  baseWithSource: object;
  baseWithSourceAndImage: object;
  article: object;
  comments: object;
  banner: object;
  marketing: object;
};

const getCSS = () => {
  const links = document.getElementsByTagName('link');

  const matchLinkElement = Array.from(links).find(link =>
    link.getAttribute('href')?.match(/freitag\/tinymce.css/),
  );

  return matchLinkElement?.getAttribute('href');
};

const dispatchChangeEvent = (editor: Editor) => {
  // See mockup commit: https://github.com/plone/mockup/commit/1a4bc145926f0d56014460621fd83bcf85fbcf82
  editor.on('change', e => {
    e.target.targetElm.value = editor.getContent();
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: false,
    });
    e.target.targetElm.dispatchEvent(changeEvent);
  });
};

const base = {
  autosave_interval: '20s',
  autosave_prefix: 'tinymce-autosave-{path}-{id}-',
  schema: 'html5-strict',
  doctype: '<!DOCTYPE html>',
  entity_encoding: 'raw',
  body_class: 'bo-content-text',
  branding: false,
  height: 100,
  menubar: false,
  statusbar: false,
  valid_elements:
    'h1,h2,' + // Allow header tags
    'a[name|href|title|target|rel=noopener],' + // Allow anchor tags with named and target attr
    '-p,' + // Allow p tags, but do not retain any attr unless specified elsewhere
    'br,' + // Allow manual linebreaks
    '-span[style|class],' + // Allow span, but only retain the style attr - used by underlinde and strikethrough
    'strong/b,em/i,' + // Allow strong and b elements, and treat them as the same - same with em and i elements
    'div[id|class]', // Allow div elements with id and class attributes.
  valid_styles: {
    p: 'text-align',
    span: 'text-decoration',
  },
  plugins: `${plugins.default} lists`,
  toolbar: `${toolbar.base} `,
  style_formats: [...styles, { block: 'h1', title: 'Überschrift' }],
  skin: false,
  content_css: getCSS(),
  relative_urls: false,
  language: 'de',
  setup(editor: Editor) {
    dispatchChangeEvent(editor);
  },
};

// Special fields for editors like Infobox, author bio, legal note, ...
const baseWithSource = {
  ...base,
  plugins: `${base.plugins} code`,
};

const baseWithSourceAndImage = {
  ...baseWithSource,
  height: 250,
  toolbar: `${toolbar.base} | placeHolderButton`,
  setup(editor: Editor) {
    placeholder(editor);

    dispatchChangeEvent(editor);
  },
};

const article = {
  ...base,
  body_class: 'bo-article-text',
  extended_valid_elements:
    '@[id|class|style|title],' + // Allow named attr for all elements
    '-sub,-sup,' + // Allow super- and subscripted text
    '-ol[type],-ul[type],-li,' + // Allow lists
    'img[src|alt|title|width|height|align],' + // Allow images
    'hr,' + // Allow horizontal rule
    '#p[class],' + // Pad p and allow class
    '-span[style|class],' + // Allow span class
    'iframe[src|width|height|name|id|class|align|style|frameborder:0|border|allowtransparency|title],' + // Allow embeds
    '-h2/h1/h3/h4/h5/h6,' + // Transform azll hx to non-empty h2
    'blockquote[class],footer[class],' + // Allow blockquote and footer for quote styles
    'audio[src|controls|preload],' + // Allow audio
    'video[src|controls|preload|width|height|poster],' + // Allow video
    'div[contenteditable|class|id|data-idx],',
  height: 500,
  statusbar: true,
  style_formats: styles,
  plugins: `${plugins.default} lists media code pagebreak`,
  toolbar: toolbar.article,
  valid_styles: {
    p: 'padding-left,text-align',
    span: 'text-decoration',
  },
  setup(editor: Editor) {
    blockquote(editor);
    infobox(editor);
    placeholder(editor);
    parallax(editor);
    paywall(editor);

    dispatchChangeEvent(editor);
  },
};

const comments = {
  ...base,
  autoresize_bottom_margin: 0,
  body_class: 'bo-comment-text',
  statusbar: false,
  plugins: `autosave autoresize`,
  toolbar: false,
  elementpath: false,
  valid_elements: 'p', // Allow p tags, but without any attributes
  setup(editor: Editor) {
    charcount(editor);

    dispatchChangeEvent(editor);
  },
};

const banner = {
  ...base,
  autoresize_bottom_margin: 0,
  valid_elements: 'a[href|title|rel=noopener],p,strong/b,em/i,',
  extended_valid_elements:
    '@[id|class|style|title],' +
    '-h2/h1/h3/h4/h5/h6,' +
    '-ol[type],-ul[type],-li,',
  height: 120,
  plugins: plugins.banner,
  toolbar: toolbar.banner,
};

const marketing = {
  autosave_interval: '20s',
  autosave_prefix: 'tinymce-autosave-{path}-{id}-',
  schema: 'html5-strict',
  doctype: '<!DOCTYPE html>',
  entity_encoding: 'raw',
  body_class: 'bo-content-text',
  branding: false,
  menubar: false,
  statusbar: false,
  style_formats: [{ block: 'p', title: 'Paragraph' }],
  skin: false,
  content_css: getCSS(),
  relative_urls: false,
  language: 'de',
  autoresize_bottom_margin: 0,
  valid_elements: 'a[href|title|rel=noopener],p,strong/b,em/i,u,s',
  extended_valid_elements:
    '@[id|class|style|title],' +
    'span[style|class|name|title|color|background-color],' +
    'div[style|class|id],' +
    'h1,h2,h3,h4,h5,h6,' +
    '-ol[type],-ul[type],-li,',
  height: 120,
  toolbar: toolbar.marketing,
  plugins: `${base.plugins} code`,
};

export const config: Setup = {
  base,
  baseWithSource,
  baseWithSourceAndImage,
  article,
  comments,
  banner,
  marketing,
};
